import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@matix/auth';
import { POS_PERMISSIONS } from '@matix/types';
import { useUI } from '../../store/ui';
import { toast } from '../../store/toast';
import { useCanI } from '../../lib/permissions';
import { useLang } from '../../lib/language';
import { useParkedOrdersStore } from '../parked-orders';
import { useHeldOrders } from '../../store/heldOrders';
import { useOutbox } from '../../store/outbox';
import { useModuleOn, useTenantConfig } from '../../store/tenantConfig';
import {
  isInstalledShell, reloadFromServer, readUpdateAvailable, subscribeUpdateAvailable,
  installShellUpdateBridge, startUpdateWatch,
} from '@matix/ui';
import { useCart } from '../../store/cart';
import {
  ShoppingCartIcon, RefreshIcon, FileTextIcon, TruckIcon, UsersIcon,
  BarChartIcon, SettingsIcon, TerminalIcon, ClockIcon, LogOutIcon,
  CloudOffIcon, PhoneIcon, GaugeIcon, TransferIcon, RouteIcon, PackageIcon, WalletOutIcon,
} from '../../components/icons';
import { useExpensesAvailable } from '../expenses';
import { useCallCenter } from '../call-center';
import { usePendingCount } from '../call-center/usePendingCount';
import { usePendingTransferCount } from '../transfers';

/**
 * Left sidebar — fixed 72px wide, primary-blue background.
 *
 * Layout:
 *   - Logo at top
 *   - Active "Sale" nav button + Parked + 3 stub nav buttons
 *   - Spacer
 *   - Settings button (stub)
 *   - User avatar with dropdown for "Close shift" + "Sign out"
 *
 * The active state of the Sale and Parked buttons mirrors the topbar's
 * `tab` field — so when the cashier is on the parked-orders screen
 * (tab === 'orders'), the Parked sidebar icon is highlighted; otherwise
 * the Sale icon is. Clicking either one switches the topbar tab, which
 * App.tsx routes to the right workspace contents.
 */
/**
 * Is a newer deploy of the till waiting on the server?
 *
 * Answered from two places at once — the page's own `/version.json` poll, which
 * is what makes this work on Android, and whatever the iOS shell reports. See
 * `reportUpdateAvailable`: the answer is the OR of them, never the latest.
 *
 * ⚠ Seeded from the LATCHED flag, not only from the event. The shell's first
 * notice can arrive before this component mounts, and an event fired then is
 * gone; reading the flag on mount is what makes the button appear for a page
 * that was already told.
 *
 * A browser gets `false` and never shows the button — it has its own reload.
 */
function useUpdateAvailable(): boolean {
  const [available, setAvailable] = useState(readUpdateAvailable);
  useEffect(() => {
    if (!isInstalledShell()) return;
    // Started HERE, next to the only thing that reads the answer, so there is
    // no second place that has to remember to start it — and nothing polls in
    // a browser session that would never show the button anyway.
    installShellUpdateBridge();
    const stopWatch = startUpdateWatch();
    const unsubscribe = subscribeUpdateAvailable(setAvailable);
    setAvailable(readUpdateAvailable());
    return () => {
      stopWatch();
      unsubscribe();
    };
  }, []);
  return available;
}

export function Sidebar() {
  const updateAvailable = useUpdateAvailable();
  const { cashier, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const setScreen = useUI((s) => s.setScreen);
  // Guards «تحديث»: the cart is in-memory, so a reload loses it.
  const cartLineCount = useCart((s) => s.lines.length);
  const tab = useUI((s) => s.tab);
  const setTab = useUI((s) => s.setTab);

  // Live count of held orders: the held INVOICES plus anything still held
  // locally on this device. Drives the badge on the Held Orders button.
  const parkedCount =
    useParkedOrdersStore((s) => s.rows.length) + useHeldOrders((s) => s.rows.length);
  // Offline sales: everything still listed drives whether the button exists,
  // the not-yet-saved ones drive the badge.
  const offlineEntries = useOutbox((s) => s.entries);
  const offlineCount = offlineEntries.length;
  const offlinePending = offlineEntries.filter(
    (e) => e.status === 'queued' || e.status === 'sending' || e.status === 'failed',
  ).length;

  // Legacy parity: "Holded Orders" gates the parked-orders list; "Order
  // History" gates the receipts/history screen. No permission → hide the entry.
  const canViewHolded = useCanI(POS_PERMISSIONS.ViewHoldedOrders);
  const canViewHistory = useCanI(POS_PERMISSIONS.ViewOrderHistory);
  // Store transfers — the legacy "Create Transfer Transaction" permission,
  // the same row the old POS gated its Transfer menu entry on. The badge is
  // requests waiting to be RECEIVED at this branch's store.
  const canTransfer = useCanI(POS_PERMISSIONS.CreateTransferTransaction);
  // The legacy Delivery Manager — its menu entry was gated on the same
  // permission, "Delivery Manger" (sic), and so is this one.
  // Business-profile modules (setup wizard) — all on for every tenant
  // onboarded before it, so these only narrow a wizard-configured till.
  const deliveryOn = useModuleOn('delivery');
  const inventoryOn = useModuleOn('inventory');
  const callCenterOn = useModuleOn('callCenter');
  const laundryOn = useModuleOn('laundry');
  const expensesOn = useExpensesAvailable();
  const canDeliveryManager = useCanI(POS_PERMISSIONS.DeliveryManager) && deliveryOn;
  const showTransfers = canTransfer && inventoryOn;
  const incomingTransfers = usePendingTransferCount(showTransfers);
  // The Reports entry is always shown: it used to need one of the four report
  // permissions, but "My Shifts" (the cashier's own sessions and their Z-report
  // reprints) is every cashier's own data, so the screen has something for
  // everyone. The permission-gated reports are still filtered inside it.

  // Call-centre orders waiting at this branch. The nav entry only exists on a
  // tenant that has the module installed AND switched on — a till that never
  // receives a phone order should never see a control for a queue it has not
  // got, exactly like the offline-sales button above.
  const ccBootstrap = useCallCenter((s) => s.bootstrap);
  const ccEnabled = Boolean(ccBootstrap?.provisioned && ccBootstrap.settings.isEnabled) && callCenterOn;
  const pendingCount = usePendingCount();

  // Does this tenant deliver at all? Same source as the order-mode pills
  // (mstr_ConfigurationParameter → IsDelivery), so the workspace and the way
  // of creating the orders it lists appear and disappear together.
  const isDeliveryTenant = useTenantConfig((s) => s.isDelivery) && deliveryOn;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const userInitial = (cashier?.username || 'U').charAt(0).toUpperCase();
  const userName = cashier?.username || t({ en: 'Cashier', ar: 'الكاشير' });
  const userMeta = `${t({ en: 'Cashier', ar: 'الكاشير' })} · ${cashier?.branch_name ?? (cashier?.branch_id != null ? `${t({ en: 'Branch', ar: 'الفرع' })} ${cashier.branch_id}` : '—')}`;

  function startOpenShift() {
    setMenuOpen(false);
    setScreen('shiftOpen');
  }

  function startCloseShift() {
    setMenuOpen(false);
    // A shift cannot be closed over unsynced sales. Expected cash is computed
    // server-side from posted receipts, so closing with sales still in the
    // queue produces a Z-report short by exactly those sales — and a variance
    // the cashier will be asked to explain and cannot. Send them to the queue
    // instead, where the Sync button is.
    if (offlinePending > 0) {
      toast(
        t({
          en: `${offlinePending} offline ${offlinePending === 1 ? 'sale is' : 'sales are'} not saved yet — sync before closing the shift`,
          ar: `${offlinePending} من المبيعات دون اتصال لم تُحفظ بعد — زامنها قبل إغلاق الوردية`,
        }),
        'error',
      );
      setTab('offline');
      return;
    }
    setScreen('shiftClose');
  }

  function handleLogout() {
    setMenuOpen(false);
    void logout();
  }

  // The "Sale" button is active whenever we're not on the parked-orders
  // view (so walkin AND delivery both highlight it). The "Parked" button
  // is the inverse.
  const onParkedView = tab === 'orders';

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">M</div>

      <button
        className={`nav-btn ${!onParkedView ? 'active' : ''}`}
        title={t({ en: 'Sale', ar: 'بيع' })}
        onClick={() => {
          // Coming back from Parked → return to whichever tab they were
          // last on; default to walk-in. We don't track previous tab so
          // walk-in is the safe choice.
          if (onParkedView) setTab('walkin');
        }}
      >
        <ShoppingCartIcon size={22} />
      </button>

      {canViewHolded && (
        <button
          className={`nav-btn ${onParkedView ? 'active' : ''}`}
          title={parkedCount === 0 ? t({ en: 'Held orders', ar: 'الطلبات المعلقة' }) : `${t({ en: 'Held orders', ar: 'الطلبات المعلقة' })} (${parkedCount})`}
          onClick={() => setTab('orders')}
        >
          <FileTextIcon size={22} />
          {parkedCount > 0 && (
            <span className="badge">{parkedCount > 99 ? '99+' : parkedCount}</span>
          )}
        </button>
      )}

      {/* Offline sales. Deliberately absent until this till has actually taken
          one — a till that never loses its network should never see a control
          for a queue it does not have. It stays visible while synced rows are
          still listed, so a cashier can match provisional numbers to invoice
          numbers after the fact. */}
      {offlineCount > 0 && (
        <button
          className={`nav-btn ${tab === 'offline' ? 'active' : ''}`}
          title={
            offlinePending > 0
              ? `${t({ en: 'Offline sales waiting', ar: 'مبيعات دون اتصال في الانتظار' })} (${offlinePending})`
              : t({ en: 'Offline sales', ar: 'المبيعات دون اتصال' })
          }
          onClick={() => setTab('offline')}
        >
          <CloudOffIcon size={22} />
          {offlinePending > 0 && (
            <span className="badge">{offlinePending > 99 ? '99+' : offlinePending}</span>
          )}
        </button>
      )}

      {ccEnabled && (
        <button
          className={`nav-btn ${tab === 'pending' ? 'active' : ''}`}
          title={
            pendingCount === 0
              ? t({ en: 'Pending orders', ar: 'الطلبات قيد الانتظار' })
              : `${t({ en: 'Pending orders', ar: 'الطلبات قيد الانتظار' })} (${pendingCount})`
          }
          onClick={() => setTab('pending')}
        >
          <PhoneIcon size={22} />
          {pendingCount > 0 && (
            <span className="badge">{pendingCount > 99 ? '99+' : pendingCount}</span>
          )}
        </button>
      )}

      {ccEnabled && (
        <button
          className={`nav-btn ${tab === 'branchMonitor' ? 'active' : ''}`}
          title={t({ en: 'Branch monitor', ar: 'متابعة الفرع' })}
          onClick={() => setTab('branchMonitor')}
        >
          <GaugeIcon size={22} />
        </button>
      )}

      {canViewHistory && (
        <button
          className={`nav-btn ${tab === "receipts" ? "active" : ""}`}
          title={t({ en: 'Receipts', ar: 'الإيصالات' })}
          onClick={() => setTab("receipts")}
        >
          <ClockIcon size={22} />
        </button>
      )}

      {/*
        Delivery hand-over. Gated on the tenant's own delivery order type, the
        same principle as the call-centre entry above: a shop that does not
        deliver should not carry a control for a queue it has not got.

        NOT gated on the call centre, deliberately — the orders this screen
        exists for are the ones with no `cc_order` row.
      */}
      {isDeliveryTenant && (
        <button
          className={`nav-btn ${tab === 'delivery' ? 'active' : ''}`}
          title={t({ en: 'Delivery', ar: 'توصيل' })}
          onClick={() => setTab('delivery')}
        >
          <TruckIcon size={22} />
        </button>
      )}
      {/* The laundry counter — only on a tenant that runs the laundry module
          (opt-in: never on for a tenant without an explicit module list). */}
      {laundryOn && (
        <button
          className={`nav-btn ${tab === 'laundry' ? 'active' : ''}`}
          title={t({ en: 'Laundry orders', ar: 'طلبات المغسلة' })}
          onClick={() => setTab('laundry')}
        >
          <PackageIcon size={22} />
        </button>
      )}
      {/* Expenses — the legacy till's expense voucher. No permission gate, as in
          the legacy menu; shown only where the branch has it set up. */}
      {expensesOn && (
        <button
          className={`nav-btn ${tab === 'expenses' ? 'active' : ''}`}
          title={t({ en: 'Expenses', ar: 'المصروفات' })}
          onClick={() => setTab('expenses')}
        >
          <WalletOutIcon size={22} />
        </button>
      )}
      {canDeliveryManager && (
        <button
          className={`nav-btn ${tab === 'deliveryManager' ? 'active' : ''}`}
          title={t({ en: 'Delivery Manager', ar: 'مدير التوصيل' })}
          onClick={() => setTab('deliveryManager')}
        >
          <RouteIcon size={22} />
        </button>
      )}
      {showTransfers && (
        <button
          className={`nav-btn ${tab === 'transfers' ? 'active' : ''}`}
          title={
            incomingTransfers > 0
              ? `${t({ en: 'Store transfers', ar: 'التحويلات المخزنية' })} (${incomingTransfers} ${t({ en: 'to receive', ar: 'للاستلام' })})`
              : t({ en: 'Store transfers', ar: 'التحويلات المخزنية' })
          }
          onClick={() => setTab('transfers')}
        >
          <TransferIcon size={22} />
          {incomingTransfers > 0 && (
            <span className="badge">{incomingTransfers > 99 ? '99+' : incomingTransfers}</span>
          )}
        </button>
      )}
      <button
        className="nav-btn"
        title={t({ en: 'Customers', ar: 'العملاء' })}
        onClick={() => toast(t({ en: 'Customers — coming soon', ar: 'العملاء — قريباً' }), 'info')}
      >
        <UsersIcon size={22} />
      </button>
      <button
        className={`nav-btn ${tab === 'reports' ? 'active' : ''}`}
        title={t({ en: 'Reports', ar: 'التقارير' })}
        onClick={() => setTab('reports')}
      >
        <BarChartIcon size={22} />
      </button>

      {/* «تحديث» — appears ONLY while a newer deploy is actually waiting, and
          disappears once this page is running it. A button that is always there
          is a button nobody reads; one that shows up is the notice itself, so
          the cashier never has to wonder whether they are on the latest till.
          In the rail rather than floating over the screen: it is a tool like
          the ones above it, it takes the till's own styling, and it follows the
          side the layout flips to for Arabic — none of which an overlay drawn
          by the app can do. Inside the app only; a browser has its own reload.
          The cart lives in memory, so updating discards it — hence the ask. */}
      {isInstalledShell() && updateAvailable && (
        <button
          className="nav-btn nav-btn-update"
          title={t({ en: 'A new version is ready — tap to update', ar: 'يوجد إصدار جديد — اضغط للتحديث' })}
          onClick={() => {
            if (
              cartLineCount > 0 &&
              !window.confirm(
                t({
                  en: 'The current sale will be discarded. Update anyway?',
                  ar: 'سيتم إلغاء الفاتورة الحالية. هل تريد التحديث؟',
                }),
              )
            ) {
              return;
            }
            reloadFromServer();
          }}
        >
          <RefreshIcon size={22} />
        </button>
      )}

      <div className="sidebar-spacer" />

      <button
        className="icon-btn sidebar-lang-btn"
        title={lang === 'ar' ? 'English' : 'العربية'}
        onClick={toggle}
      >
        {lang === 'ar' ? 'EN' : 'ع'}
      </button>

      <button
        className="icon-btn"
        title={t({ en: 'Settings', ar: 'الإعدادات' })}
        style={{ color: 'rgba(255,255,255,0.7)' }}
        // Settings live in the admin app. Opened in a new tab so the till's
        // cart and shift stay where they are; the admin app has its own login.
        onClick={() => {
          const url = (import.meta.env as Record<string, string | undefined>).VITE_ADMIN_URL ?? 'http://localhost:5175';
          window.open(url, '_blank', 'noopener');
        }}
      >
        <SettingsIcon size={22} />
      </button>

      <div ref={menuRef} style={{ position: 'relative' }}>
        <div
          className="sidebar-user"
          title={userName}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {userInitial}
        </div>
        {menuOpen && (
          <div
            className="dropdown show"
            // Open the menu toward the main content, on whichever side the
            // sidebar sits. In RTL the sidebar flips to the right edge, so a
            // hard-coded `left: 60` pushed the menu off-screen and its items
            // (Sign out included) couldn't be clicked. Logical `inset-inline`
            // maps to left in LTR and right in RTL automatically.
            style={{ bottom: 60, top: 'auto', insetInlineStart: 60, insetInlineEnd: 'auto' }}
          >
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                {userName}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {userMeta}
              </div>
            </div>
            <div className="dropdown-item" onClick={startOpenShift}>
              <ClockIcon size={14} />
              {t({ en: 'Open shift', ar: 'فتح الوردية' })}
            </div>
            <div className="dropdown-item" onClick={startCloseShift}>
              <TerminalIcon size={14} />
              {t({ en: 'Close shift & Z-report', ar: 'إغلاق الوردية وتقرير Z' })}
            </div>
            <div className="dropdown-divider" />
            <div
              className="dropdown-item"
              onClick={() => { setMenuOpen(false); toggle(); }}
            >
              <span
                style={{
                  fontWeight: 700, fontSize: 11, lineHeight: 1.4,
                  border: '1px solid var(--border)', borderRadius: 4,
                  padding: '0 5px', minWidth: 22, textAlign: 'center',
                }}
              >
                {lang === 'ar' ? 'EN' : 'ع'}
              </span>
              {lang === 'ar' ? 'English' : 'العربية'}
            </div>
            <div className="dropdown-divider" />
            <div className="dropdown-item danger" onClick={handleLogout}>
              <LogOutIcon size={14} />
              {t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
